"use client"

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

// Коэффициенты чувствительности точек: центральные реагируют сильнее, крайние — слабее
const GAINS = [0.55, 0.85, 1, 0.85, 0.55]
const MIN_SCALE = 0.28
const LERP_UP = 0.45
const LERP_DOWN = 0.18

interface VoiceEqualizerProps {
  /** Читает текущий уровень 0..1 без ререндера */
  getLevel: () => number
  className?: string
}

/**
 * Пять точек-столбиков, масштаб каждой следует за уровнем громкости с инерцией (lerp).
 * Анимация в rAF напрямую через style.transform — без setState.
 */
export function VoiceEqualizer({ getLevel, className }: VoiceEqualizerProps) {
  const barsRef = useRef<Array<HTMLSpanElement | null>>([])
  const currentRef = useRef<number[]>(GAINS.map(() => MIN_SCALE))

  useEffect(() => {
    let raf = 0
    let t = 0
    const tick = () => {
      t += 1
      const level = getLevel()
      for (let i = 0; i < GAINS.length; i++) {
        // Лёгкая асинхронность точек, чтобы они не двигались как одна
        const wobble = 1 + 0.12 * Math.sin(t / 6 + i * 1.7)
        const target = Math.max(MIN_SCALE, Math.min(1, MIN_SCALE + level * GAINS[i] * wobble * (1 - MIN_SCALE) * 1.4))
        const current = currentRef.current[i]
        const k = target > current ? LERP_UP : LERP_DOWN
        const next = current + (target - current) * k
        currentRef.current[i] = next
        const el = barsRef.current[i]
        if (el) el.style.transform = `scaleY(${next.toFixed(3)})`
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [getLevel])

  return (
    <span className={cn('flex h-4 items-center gap-[3px]', className)} aria-hidden="true">
      {GAINS.map((_, i) => (
        <span
          key={i}
          ref={(el) => { barsRef.current[i] = el }}
          className="block h-full w-[3px] rounded-full bg-current origin-center will-change-transform"
          style={{ transform: `scaleY(${MIN_SCALE})` }}
        />
      ))}
    </span>
  )
}
